import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Grid, ArrowRight, Calendar, MapPin } from 'lucide-react';
import { fairManagerApi } from '../services/api';
import { Fair } from '../types';
import { extractApiError } from '../utils/errorUtils';
import { formatDateRange } from '../utils/fairStatus'; // Issue #1672

export default function FairManagerSlotsPage() {
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFairs();
  }, []);

  const loadFairs = async () => {
    try {
      setLoading(true);
      const data = await fairManagerApi.getFairs();
      setFairs(data);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load managed fairs:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6" data-testid="slots-page-heading">Slot Management</h1>
        <div className="text-center py-12" data-testid="slots-loading">
          <p className="text-gray-500">Loading your fairs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6" data-testid="slots-page-heading">Slot Management</h1>
        <div className="text-center py-12" data-testid="slots-error">
          <p className="text-red-500" data-testid="slots-error-message">{error}</p>
          <button
            onClick={loadFairs}
            className="mt-4 px-4 py-2 bg-teal-500 text-white rounded hover:bg-primary"
            data-testid="slots-page-try-again-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (fairs.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6" data-testid="slots-page-heading">Slot Management</h1>
        <div className="text-center py-12" data-testid="slots-empty-state">
          <Grid className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">No Fairs Yet</h2>
          <p className="text-gray-500 mb-6" data-testid="slots-empty-message">
            Create a fair to start managing vendor booth slots
          </p>
          <Link
            to="/fairmanager/fair/new"
            className="inline-flex items-center px-4 py-2 bg-teal-500 text-white rounded hover:bg-primary"
            data-testid="create-fair-button"
          >
            Create New Fair
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="slots-page-heading">Slot Management</h1>
        <p className="text-gray-600">
          Select a fair to manage its vendor booth slots
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="fair-cards-list">
        {fairs.map((fair) => (
          <div key={fair.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow" data-testid="fair-card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3" data-testid="fair-name">
              {fair.name}
            </h3>

            <div className="space-y-2 mb-4">
              {fair.start_date && fair.end_date && (
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>
                    {formatDateRange(fair.start_date, fair.end_date)}
                  </span>
                </div>
              )}

              {fair.city && fair.state && (
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>{fair.city}, {fair.state}</span>
                </div>
              )}

              {fair.total_slots !== undefined && (
                <div className="text-sm text-gray-600">
                  Total Slots: <span className="font-semibold">{fair.total_slots}</span>
                </div>
              )}
            </div>

            <Link
              to={`/fairmanager/fairs/${fair.id}/slots`}
              className="inline-flex items-center justify-center w-full px-4 py-2 bg-teal-500 text-white rounded hover:bg-primary transition-colors"
              data-testid="manage-slots-link"
            >
              Manage Slots
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
